import AxiosService from '@/api/ApiBase';
import {
  IpiLoadGuideCreateNotePayload,
  IpiLoadGuideCreatePayload,
  IpiLoadGuideDeleteNotePayload,
  IpiLoadGuideFilters,
  IpiLoadGuideFinalizeResponse,
  IpiLoadGuideResponse,
  IpiLoadGuideTrackingFilters,
  IpiLoadGuideUpdateNotePayload,
  IpiLoadGuideUpdatePayload,
  IpiLoadGuideUpdateSealDetailsPayload,
  IpiLoadTruckingResponse,
  IpiUpdateSealDetailCommissionAblePayload,
  LoadGuideAmendPayload,
  LoadGuideProfitLossResponse,
  IpiLoadGuideDevicesResponse,
} from '@/types/ipi/load-guide/ipi.load-guide.types';
import { IpiLoadGuide } from '@/types/ipi/load-guide/ipi.load-guide.entity';
import { IpiLoadGuideHandOffPayload } from '@/types/ipi/load-guide/ipi.load-guide-hand-off.types';
import { FilterOptionsParams } from '@/types/service/filters.service';
import { VirtualSearchPayload, VirtualSearchResponse } from '@/types/virtual-search';
import { IpiLoadModeChangePayload } from '@/types/ipi/load-plan/ipi.load-plan.types';

const LOAD_GUIDE_BASE_URL = 'warehouse/ipi/loadGuide';

// const LOAD_GUIDE_BASE_URL_1 = 'http://localhost:3000/api/ipi/loadGuide';

class IpiLoadGuideService {
  get(id: string) {
    return AxiosService.get<IpiLoadGuide>(`${LOAD_GUIDE_BASE_URL}/getDetails`, {
      params: {
        loadGuideId: id,
      },
    });
  }

  getAll(filters: IpiLoadGuideFilters) {
    return AxiosService.get<IpiLoadGuideResponse>(`${LOAD_GUIDE_BASE_URL}/getAll`, {
      params: filters,
    });
  }

  virtualSearch(payload: VirtualSearchPayload<IpiLoadGuide>) {
    return AxiosService.post<VirtualSearchResponse<IpiLoadGuide>>(
      'warehouse/v2/ipiLoading/loadGuide/search',
      payload
    );
  }

  getFilterOptions<T>(params: Omit<FilterOptionsParams, 'limit'>) {
    return AxiosService.get<T>(`${LOAD_GUIDE_BASE_URL}/filter/options`, {
      params: { ...params, limit: 100 },
    });
  }

  getTrackingFilterOptions<T>(params: Omit<FilterOptionsParams, 'limit'>) {
    return AxiosService.get<T>(`warehouse/ipi/tracking/filter/options`, {
      params: { ...params, limit: 100 },
    });
  }

  getTrackingList(filters: IpiLoadGuideTrackingFilters) {
    return AxiosService.get<IpiLoadTruckingResponse>(`${LOAD_GUIDE_BASE_URL}/tracking`, {
      params: filters,
    });
  }

  create(payload: IpiLoadGuideCreatePayload) {
    return AxiosService.post<IpiLoadGuide>(`${LOAD_GUIDE_BASE_URL}/create`, payload);
  }

  delete(id: string) {
    return AxiosService.delete(`${LOAD_GUIDE_BASE_URL}/delete/${id}`);
  }

  updateHandoff({
    tirDoc,
    deliveryOrderDoc,
    driverLicenseCopy,
    _id,
    ...payload
  }: IpiLoadGuideHandOffPayload) {
    const formData = new FormData();

    formData.append('payload', JSON.stringify(payload));

    tirDoc && formData.append('attachments', tirDoc, tirDoc.name);
    deliveryOrderDoc && formData.append('attachments', deliveryOrderDoc, deliveryOrderDoc.name);
    driverLicenseCopy && formData.append('attachments', driverLicenseCopy, driverLicenseCopy.name);

    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/updateHandOff/${_id}`, formData);
  }

  updateDepartureDate(payload: { loadGuideId: string; truckDepartureDate: Date }) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/update/truckDepartureDate`, payload);
  }

  amend(payload: LoadGuideAmendPayload) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/amend`, payload);
  }

  update(payload: Partial<IpiLoadGuideUpdatePayload>) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/details`, payload);
  }

  updateSealDetails(payload: IpiLoadGuideUpdateSealDetailsPayload) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/sealDetails`, payload);
  }

  updateNotesForLoadGuide(payload: IpiLoadGuideUpdateNotePayload) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/notes`, payload);
  }

  createNoteForLoadGuide(payload: IpiLoadGuideCreateNotePayload) {
    return AxiosService.post(`${LOAD_GUIDE_BASE_URL}/notes`, payload);
  }

  deleteNoteFromLoadGuide(payload: IpiLoadGuideDeleteNotePayload) {
    return AxiosService.delete(`${LOAD_GUIDE_BASE_URL}/notes`, {
      params: payload,
    });
  }

  getProfitLoss(loadGuideId: string) {
    return AxiosService.get<LoadGuideProfitLossResponse>(`${LOAD_GUIDE_BASE_URL}/profitAndLoss`, {
      params: { loadGuideId },
    });
  }

  finalize(loadGuideId: string) {
    return AxiosService.put<IpiLoadGuideFinalizeResponse>(
      `${LOAD_GUIDE_BASE_URL}/finalize/${loadGuideId}`
    );
  }

  updateSealDetailCommissionAble({ _id, ...payload }: IpiUpdateSealDetailCommissionAblePayload) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/sealDetails/commissionAble/${_id}`, payload);
  }

  removeHandoffs(id: string) {
    return AxiosService.delete(`${LOAD_GUIDE_BASE_URL}/handOff/${id}`);
  }

  changeLoadMode(payload: IpiLoadModeChangePayload) {
    return AxiosService.patch(`${LOAD_GUIDE_BASE_URL}/loadMode`, payload);
  }

  getLoadGuideDevicesList() {
    return AxiosService.get<IpiLoadGuideDevicesResponse>(`/track/gt06/devices`);
  }

  startDeviceTracking({
    deviceType,
    deviceId,
    referenceId,
    startTrackTime,
  }: {
    deviceType: string;
    deviceId: string;
    referenceId: string;
    startTrackTime: string;
  }) {
    return AxiosService.post(`/track/otr`, {
      deviceType,
      deviceId,
      referenceId,
      referenceEntityType: 'load',
      startTrackTime,
    });
  }

  stopDeviceTracking({
    referenceId,
    referenceType,
    endTrackTime,
  }: {
    referenceId: string;
    referenceType: string;
    endTrackTime: string;
  }) {
    return AxiosService.post(`/track/otr/stop-track/${referenceId}/${referenceType}`, {
      endTrackTime,
    });
  }

  getTrackingResults(referenceId: string, referenceType: string) {
    return AxiosService.get<any>(`/track/otr/${referenceId}/${referenceType}`);
  }
}

export default new IpiLoadGuideService();
